/*
 * Filename: d:\projects\AutoFiFront\src\Services\Pictures\PicturesService.ts
 * Path: d:\projects\AutoFiFront
 * Created Date: Wednesday, January 25th 2023, 12:04:23 pm
 * Author: Jonathan MAZAT
 *
 * Copyright (c) 2023 Autobuy SAS
 */

import Config from "Config/Config";
import { PictureType } from "Content/Car/CarImgSlider/CarImgSliderProps.type";
import Compressor from "compressorjs";
import Axios from "axios";
import AuthenticationError from "Error/AuthenticationError";
import ServiceGeneral from "Services/ServiceGeneral";
import { RespBuyBackReqDbType } from "Services/BuyBack/BuyBackType";

type callbackType = (
  regNb: string,
  carId: number,
  file: string
) => Promise<any>;

type PictureSendingType = {
  name: string;
  immat: string;
  CarID: number;
  pictureb64: string;
};

export default class PictureService {
  static compressImage(
    image: File,
    carPromise: Promise<RespBuyBackReqDbType>,
    callback: callbackType
  ) {
    new Compressor(image, {
      mimeType: "image/jpeg",
      quality: 0.6,

      // The compression process is asynchronous,
      // which means you have to access the `result` in the `success` hook function.
      async success(result) {
        const carDb = await carPromise;
        if (result instanceof File) {
          const myResult = await PictureService.transformPicToBase64(result);
          const ret = await callback(carDb.Registration, carDb.id, myResult);
        } else if (result instanceof Blob) {
          const myResult = await PictureService.blobToBase64(result);
          const ret = await callback(carDb.Registration, carDb.id, myResult);
        }
      },
      error(err: Error) {
        console.log(err.message);
      },
    });
  }

  static transformPicToBase64(file: File): Promise<string> {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onerror = () => {
        reader.abort();
        reject(new DOMException("Problem parsing input file."));
      };

      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(file);
    });
  }

  static async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  }

  static async pushToServ(
    name: string,
    pictureArray: any[],
    immat: string,
    offerId: number,
    pictureb64: string
  ) {
    const picture: PictureSendingType = {
      immat,
      name,
      CarID: offerId,
      pictureb64: pictureb64,
    };

    try {
      const response = await Axios.post(
        Config.BACKEND.BASEURL + Config.BACKEND.PATHS.ADD_BUYBACK_PICTURE,
        picture
      );
      if (
        (await ServiceGeneral.handleAxiosResponse(await response)) === "end"
      ) {
        return undefined;
      }
      pictureArray.push(response);
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async uploadPicture(
    file: File,
    carPromise: Promise<RespBuyBackReqDbType>,
    name: string,
    pictureArray: any[]
  ) {
    return PictureService.compressImage(
      file,
      carPromise,
      PictureService.pushToServ.bind(null, name, pictureArray)
    );
  }

  static async propagateEmails(id: number) {
    try {
      const response = Axios.get(
        `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.PROPAGATE_BUYBACK}/${id}`
      );
      return response;
    } catch (e) {
      if (e instanceof AuthenticationError) throw e;
      const data = ServiceGeneral.interpretError(e);
      return data;
    }
  }

  static async getFirstPicture(id: number): Promise<any> {
    const url =
      Config.BACKEND.BASEURL + Config.BACKEND.PATHS.GET_FIRST_PICTURE + `${id}`;
    const myPictures: PictureType[] = await (await fetch(url)).json();
    return myPictures;
  }
}
